import { Calendar, Search, X, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { tt } from '@agos/shared'

import { cn } from '../cn'

// ─── SearchInput ─────────────────────────────────────────────────────────────

interface SearchInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
}

export function SearchInput({ value, onChange, placeholder, className }: SearchInputProps) {
  return (
    <div className={cn('relative', className)}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        // FB-006: legacy's default reads "Hladat..." — the diacritics were
        // stripped. sk.json spells it "Hľadať".
        placeholder={placeholder ?? tt('Hľadať...')}
        className="w-full pl-9 pr-8 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label={tt('Vymazať')}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  )
}

// ─── FilterSelect / FilterBar ────────────────────────────────────────────────

interface FilterOption {
  value: string
  label: string
  count?: number
}

interface FilterSelectProps {
  value: string
  onChange: (value: string) => void
  options: FilterOption[]
  className?: string
  /** What this select filters. Legacy ships them unlabelled; a screen reader reads
   *  only the current option, which on a filter bar of three says nothing. */
  label?: string
  /**
   * Draw the `label` above the control — §9.192.
   *
   * **Opt-in, with 34 call sites.** Nearly all of them sit in a horizontal `FilterBar` where a
   * caption above each would reflow a row nobody has reviewed; the screens that need it are the ones
   * laying filters out in a grid. The reasoning behind `label` was right and stopped one step short:
   * a sighted operator also reads only the current option, so a panel of six selects all showing
   * *Všetci* says nothing about what any of them filters.
   */
  showLabel?: boolean
}

export function FilterSelect({
  value,
  onChange,
  options,
  className,
  label,
  showLabel = false,
}: FilterSelectProps) {
  const select = (
    <select
      value={value}
      aria-label={label}
      onChange={(event) => onChange(event.target.value)}
      className={cn(
        'w-full sm:w-auto px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all',
        className,
      )}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
          {option.count != null ? ` (${option.count})` : ''}
        </option>
      ))}
    </select>
  )

  if (!showLabel || label === undefined) return select

  return (
    <label className="block space-y-1">
      <span className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {select}
    </label>
  )
}

interface FilterBarProps {
  search?: { value: string; onChange: (value: string) => void; placeholder?: string }
  filters?: ReactNode
  actions?: ReactNode
  className?: string
}

export function FilterBar({ search, filters, actions, className }: FilterBarProps) {
  return (
    <div
      className={cn(
        'flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3 mb-4',
        className,
      )}
    >
      {search && (
        <SearchInput
          value={search.value}
          onChange={search.onChange}
          placeholder={search.placeholder}
          className="w-full sm:w-64"
        />
      )}
      {filters}
      {actions && <div className="flex items-center gap-2 flex-wrap sm:ml-auto">{actions}</div>}
    </div>
  )
}

// ─── DateRangePicker ─────────────────────────────────────────────────────────

interface DateRangePickerProps {
  from: string
  to: string
  onFromChange: (value: string) => void
  onToChange: (value: string) => void
  className?: string
}

export function DateRangePicker({
  from,
  to,
  onFromChange,
  onToChange,
  className,
}: DateRangePickerProps) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className="relative">
        <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <input
          type="date"
          value={from}
          aria-label={tt('Od')}
          onChange={(event) => onFromChange(event.target.value)}
          className="pl-8 pr-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all"
        />
      </div>
      <span className="text-xs text-muted-foreground">-</span>
      <div className="relative">
        <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <input
          type="date"
          value={to}
          aria-label={tt('Do')}
          onChange={(event) => onToChange(event.target.value)}
          className="pl-8 pr-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all"
        />
      </div>
    </div>
  )
}

// ─── ButtonGroup ─────────────────────────────────────────────────────────────

interface ButtonGroupOption {
  value: string
  label: string
  icon?: LucideIcon
  count?: number
}

interface ButtonGroupProps {
  options: ButtonGroupOption[]
  value: string
  onChange: (value: string) => void
  className?: string
  size?: 'sm' | 'md'
}

export function ButtonGroup({
  options,
  value,
  onChange,
  className,
  size = 'sm',
}: ButtonGroupProps) {
  return (
    <div className={cn('flex flex-wrap gap-1.5', className)}>
      {options.map((option) => {
        const isActive = value === option.value
        const Icon = option.icon
        return (
          <button
            key={option.value}
            type="button"
            // Which option is chosen was carried by colour alone, so a screen reader read seven
            // identical buttons and a test could not tell the selected period from the rest.
            aria-pressed={isActive}
            onClick={() => onChange(option.value)}
            className={cn(
              'flex items-center gap-1.5 rounded-xl font-bold border transition-all',
              size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3.5 py-2 text-sm',
              isActive
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/30',
            )}
          >
            {Icon && <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />}
            {option.label}
            {option.count != null && (
              <span
                className={cn(
                  'px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none',
                  isActive
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {option.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ─── TabGroup ────────────────────────────────────────────────────────────────

interface Tab {
  id: string
  label: string
  icon?: LucideIcon
  count?: number
}

interface TabGroupProps {
  tabs: Tab[]
  active: string
  onChange: (id: string) => void
  className?: string
  /** `pills` = rounded buttons; `bar` = the underlined section tabs. */
  variant?: 'pills' | 'bar'
}

export function TabGroup({ tabs, active, onChange, className, variant = 'pills' }: TabGroupProps) {
  const isBar = variant === 'bar'

  return (
    <div
      className={cn(
        isBar
          ? 'flex flex-nowrap sm:flex-wrap gap-0 border-b border-border mb-4 overflow-x-auto'
          : 'flex flex-wrap gap-1.5 mb-4',
        className,
      )}
    >
      {tabs.map((tab) => {
        const isActive = active === tab.id
        const Icon = tab.icon
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={cn(
              isBar
                ? 'flex items-center gap-2 px-3 sm:px-5 py-2.5 sm:py-3 text-sm font-semibold transition-all relative shrink-0 sm:shrink whitespace-nowrap'
                : 'flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all',
              isBar
                ? isActive
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
                : isActive
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/30',
            )}
          >
            {Icon && <Icon className={isBar ? 'w-4 h-4' : 'w-3.5 h-3.5'} />}
            {tab.label}
            {tab.count != null && (
              <span
                className={cn(
                  'px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none',
                  isBar ? '' : 'ml-0.5',
                  isActive
                    ? isBar
                      ? 'bg-primary/15 text-primary'
                      : 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {tab.count}
              </span>
            )}
            {isBar && isActive && (
              <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full" />
            )}
          </button>
        )
      })}
    </div>
  )
}
